import { matomoState } from '@/utils/matomo/state'

export const useMatomo = () => {
    /**
     * Get the Matomo tracker instance from the state object.
     *
     * @returns - The Matomo tracker instance
     */
    const withMatomo = (fn: (tracker: any) => void) => {
        const tracker = window.Matomo?.getAsyncTracker()

        if (tracker && matomoState.ready) {
            fn(tracker)
        } else {
            matomoState.queue.push(fn)
        }
    }

    /**
     * Track an event with Matomo
     *
     * @param category - The Event Category (Videos, Music, Games, etc.)
     * @param action - The Event Action (Play, Pause, Duration, Add Playlist, etc.)
     * @param name - Optional: The events object name (eg. the video title, the song name, etc.)
     * @param value - Optional: The events object value (eg. the video duration, the song length, etc.)
     * @param callback
     * @param customData
     */
    const trackEvent = (
        category: string,
        action: string,
        name?: string,
        value?: number,
        callback?: () => void,
        customData?: any,
    ) => {
        withMatomo((matomo) => {
            matomo?.trackEvent(
                category,
                action,
                name,
                value,
                callback,
                customData)
        });
    };
    /**
     *  Get the full URL of a route.
     *
     * @param router - The router instance
     * @param path - The path of the route
     * @returns - The full URL of the route
     */
    const getFullUrl = (router: Router, path?: string) => {
        if (!path) return;

        return `${window.location.origin}${router.resolve(path).href}`;
    };

    const trackPageView = async (nuxt: any, to: any, from: any) => {
        await new Promise<void>((resolve) => {
            withMatomo((matomo) => {
                console.debug('[Matomo]: IS TRUE')
                const {
                    path,
                    viewName,
                    name
                } = nuxt.$client.page;

                switch (viewName) {
                    case 'category-articles':
                        matomo.setEcommerceView(
                            false,
                            false,
                            path);
                        break;
                    case 'article-overview':
                        // No action. See the product tracking further down.
                        break;
                    default:
                }

                const referrerUrl = getFullUrl(nuxt.$router, from?.fullPath); // `${window.location.origin}${nuxt.$router.resolve(path).href}`;
                const url = getFullUrl(nuxt.$router, to?.fullPath); // `${window.location.origin}${nuxt.$router.resolve(to.fullPath).href}`;

                const { authenticated, user }: any = nuxt.$authentication;

                if (referrerUrl) matomo.setReferrerUrl(referrerUrl);
                if (url) matomo.setCustomUrl(url);
                if (name) matomo.setDocumentTitle(name);
                if (authenticated) matomo.setUserId(user.customerId);

                //if (options.debug) {
                //    console.debug(`[Matomo] Tracking page view for ${url}`);
                //}

                matomo.trackPageView();

                nextTick().then(() => setTimeout(resolve, 0));
            });
        });
    }

    const trackEcommerceOrder = (order: any) => {
        withMatomo((matomo) => {
            matomo?.trackEcommerceOrder(
                order.orderReference.toString(), // (Required) – String – The order ID.
                order.statisticalPrice.inclusive, // grandTotal (Required) – Integer/Float – The amount of revenue generated from your sales (including tax, shipping, and subtracted discounts). See how much you’ve made within specific periods at a glance.
                order.statisticalPrice.subTotal, // subTotal (Optional) – Integer/Float The order subtotal, excluding shipping and tax. This can help you get a better understanding of your sales revenue without having to account for varying regional and international charges.
                order.statisticalPrice.vat, // tax (Optional) – Integer/Float The total amount of tax charged on all sales. Tracking or excluding this value can help you to understand your pre-tax and post-tax profits across your website.
                order.statisticalPrice.shipping, // shipping (Optional) – Integer/Float – The total shipping costs charged on all sales. When plotted against revenue, you can work out the effectiveness of free shipping offers.
                order.statisticalPrice.discount // discount (Optional) – Integer/Float/Boolean – The total discount applied to all sales. This helps to quantify the effective marketing costs of discount
            )
        });
    }

    const addOrRemoveEcommerceItems = (items: any, total: number) => {
        withMatomo((matomo) => {
            console.log('[Matomo]: Adding and removing ecommerce items.');

            Object.values(items).forEach((item: any) => {
                if (item.quantity > 0) {
                    console.log('[Motomo]: Adding item:', item.name);
                    matomo.addEcommerceItem(
                        item.code,
                        item.name,
                        item.category,
                        item.prices.item.statistic,
                        item.quantity
                    );
                } else {
                    console.log('[Motomo]: Removing item:', item.name);
                    matomo.removeEcommerceItem(item.code);
                }
            });

            matomo.trackEcommerceCartUpdate(total);
        });
    }

    return {
        trackEvent,
        trackEcommerceOrder,
        addOrRemoveEcommerceItems,
        trackPageView
    };
};